import {
  Component,
  computed,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Card genérica reutilizable con estilo "Seekop".
 *
 * Usualmente se usa en pantallas tipo login o formularios para envolver un
 * contenido con padding, esquinas redondeadas y sombra.
 *
 * Nota: El estilo de "card" (background + shadow) solo se aplica cuando
 * `isNarrow` es `true`. En layouts amplios puede usarse como contenedor simple.
 *
 * Configuración de sombra:
 * - `showShadow`: activa/desactiva la sombra (por defecto: true)
 * - `shadowIntensity`: define la intensidad ('light', 'medium', 'strong')
 * - `shadow`: permite sobrescribir completamente con un valor CSS personalizado
 */
@Component({
  selector: 'so-card-ui',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './so-card.component.html',
  styleUrls: ['./so-card.component.scss'],
})
export class SoCardUI {
  /** Si `true`, aplica decoración tipo card (background, radius, shadow). */
  isNarrow = input<boolean>(true);

  /** Ancho del contenedor. Si es `null`, usa un default basado en `isNarrow`. */
  width = input<string | null>(null);

  /** Máximo ancho permitido. Si es `null`, usa un default basado en `isNarrow`. */
  maxWidth = input<string | null>(null);

  /** Padding interno del card. Si es `null`, usa un default basado en `isNarrow`. */
  padding = input<string | null>(null);

  /** Color de fondo del card. Si es `null`, se resuelve según el tema. */
  backgroundColor = input<string | null>(null);

  /** Si `true`, aplica modo oscuro manualmente (sobrescribe detección automática). */
  darkMode = input<boolean | null>(null);

  /** Si `true`, muestra la sombra del card (solo aplica cuando isNarrow es true). */
  showShadow = input<boolean>(true);

  /** Intensidad de la sombra: 'light', 'medium', 'strong'. Solo aplica si showShadow es true. */
  shadowIntensity = input<'light' | 'medium' | 'strong'>('medium');

  /** Permite sobrescribir completamente la sombra del card. Ej: `none` para quitarla o un valor CSS personalizado. */
  shadow = input<string | null>(null);

  /** Estilos computados para el contenedor */
  containerStyles = computed(() => {
    const styles: Record<string, string> = {};

    // Width - siempre 100% por defecto
    const w = this.width();
    if (w) {
      styles['width'] = w;
    }

    // Max width - solo si se especifica
    const mw = this.maxWidth();
    if (mw) {
      styles['max-width'] = mw;
    }

    // Padding
    const p = this.padding();
    if (p) {
      styles['padding'] = p;
    }

    // Background color
    const bg = this.backgroundColor();
    if (bg) {
      styles['background-color'] = bg;
    }

    // Shadow - prioridad: shadow personalizado > showShadow + shadowIntensity
    const customShadow = this.shadow();
    if (customShadow) {
      styles['box-shadow'] = customShadow;
    } else if (this.isNarrow() && this.showShadow()) {
      const intensity = this.shadowIntensity();
      const shadowMap = {
        'light': '0 4px 12px rgba(0, 0, 0, 0.08)',
        'medium': '0 10px 30px rgba(0, 0, 0, 0.2)',
        'strong': '0 15px 40px rgba(0, 0, 0, 0.3)'
      };
      styles['box-shadow'] = shadowMap[intensity];
    } else if (!this.showShadow()) {
      styles['box-shadow'] = 'none';
    }

    return styles;
  });

  /** Clases CSS computadas */
  containerClasses = computed(() => {
    return {
      'so-card': true,
      'so-card--narrow': this.isNarrow(),
      'so-card--wide': !this.isNarrow(),
      'so-card--dark': this.darkMode() === true,
    };
  });

}
